// @ts-nocheck
import { mapState } from "../map/state";
import { update } from "../map/camera";
import { ensureHeadstoneInDOM } from "../map/holes";
import { api } from "../api/client";
import { backendReadyPromise, fetchViewportIfNeeded, indexHeadstone } from "../map/data-sync";
import { navigateToHeadstone } from "../map/headstone-nav";
import { closeViewModal } from "../map/grave-render";
import { holeIdFromCoords, coordsFromHoleId } from "../utils/hole-id";
import { showToast } from "../components/toast/toast";
import { t } from "../i18n";
import type { LegacyHeadstone } from "../utils/dto";
import {
  parseGardenLocation,
  runAsGardenLocationApply,
  type GardenLocationTarget,
} from "./garden-location";

export type MemorialDeepLinkTarget = GardenLocationTarget;

function isStripeReturn(params: URLSearchParams): boolean {
  return !!(
    params.get("session_id") ||
    params.get("success") === "true" ||
    params.get("canceled") === "true"
  );
}

export function parseMemorialDeepLinkParams(
  params?: URLSearchParams,
): MemorialDeepLinkTarget | null {
  return parseGardenLocation(params);
}

export function hasMemorialDeepLinkParams(
  params?: URLSearchParams,
): boolean {
  return parseMemorialDeepLinkParams(
    params ?? new URLSearchParams(window.location.search),
  ) !== null;
}

function resolveLegacy(target: MemorialDeepLinkTarget): LegacyHeadstone | null {
  if (target.graveId) {
    return (
      Object.values(mapState.gravesData).find((g) => g.id === target.graveId) ??
      null
    );
  }
  if (target.holeId) {
    return mapState.gravesData[target.holeId] ?? null;
  }
  if (target.x !== undefined && target.y !== undefined) {
    const hid = holeIdFromCoords(target.x, target.y);
    return mapState.gravesData[hid] ?? null;
  }
  return null;
}

function shouldOpenModal(target: MemorialDeepLinkTarget): boolean {
  return target.openModal ?? Boolean(target.graveId);
}

/** Center map on memorial; open view modal when requested and a headstone exists. */
export function navigateToMemorial(target: MemorialDeepLinkTarget): boolean {
  const legacy = resolveLegacy(target);
  const openModal = shouldOpenModal(target);

  let bx: number;
  let by: number;
  if (legacy) {
    [bx, by] = coordsFromHoleId(legacy.holeId);
  } else if (target.x !== undefined && target.y !== undefined) {
    bx = target.x;
    by = target.y;
  } else if (target.holeId) {
    [bx, by] = coordsFromHoleId(target.holeId);
  } else {
    return false;
  }

  const { hole, holeId, x, y } = ensureHeadstoneInDOM(bx, by);
  mapState.camX = x;
  mapState.camY = y;
  update();

  fetchViewportIfNeeded().catch(() => null);

  if (legacy) return navigateToHeadstone(legacy, { openModal });

  if (target.graveId) return false;

  if (hole) {
    hole.classList.add("pulse");
    setTimeout(() => hole.classList.remove("pulse"), 900);
  }
  return true;
}

async function hydrateGraveById(graveId: string): Promise<boolean> {
  const { data, error } = await api.GET("/headstones");
  if (error || !Array.isArray(data)) return false;
  data.forEach(indexHeadstone);
  return Object.values(mapState.gravesData).some((g) => g.id === graveId);
}

async function tryNavigateWithRetry(
  target: MemorialDeepLinkTarget,
  retriesLeft: number,
): Promise<boolean> {
  if (navigateToMemorial(target)) return true;
  if (retriesLeft <= 0) {
    if (target.graveId) {
      await hydrateGraveById(target.graveId);
      return navigateToMemorial(target);
    }
    return false;
  }
  await new Promise((resolve) => setTimeout(resolve, 1500));
  return tryNavigateWithRetry(target, retriesLeft - 1);
}

function applyGardenLocationFromWindow(): void {
  const target = parseMemorialDeepLinkParams();
  runAsGardenLocationApply(() => {
    if (!target) {
      closeViewModal({ persistGardenLocation: false });
      return;
    }
    if (!shouldOpenModal(target)) {
      closeViewModal({ persistGardenLocation: false });
    }
    const ok = navigateToMemorial(target);
    if (!ok && shouldOpenModal(target)) {
      showToast(t("memorial.notFound"));
    }
  });
}

export function initMemorialDeepLink(): void {
  window.addEventListener("popstate", () => {
    applyGardenLocationFromWindow();
  });

  const params = new URLSearchParams(window.location.search);
  if (isStripeReturn(params)) return;

  const target = parseMemorialDeepLinkParams(params);
  if (!target) return;

  const timeoutPromise = new Promise<void>((resolve) =>
    setTimeout(resolve, 8000),
  );
  Promise.race([backendReadyPromise, timeoutPromise]).then(async () => {
    const ok = await tryNavigateWithRetry(target, 2);
    if (!ok && shouldOpenModal(target)) {
      showToast(t("memorial.notFound"));
    }
  });
}
